import { v4 as uuidv4 } from "uuid";
import userImagePlaceholder from "@circle-react-shared/BuilderUI/Assets/user_1_image_placeholder.png";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { getMarginOptions } from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { createUserCardBlock } from "../../utils/layoutBlockHelpers/userCardBlockHelper";
import { createImageBlock } from "../../utils/mediaBlockHelpers";
import {
  createH2Block,
  createH4Block,
  createPBlock,
  createQuoteBlock,
  createUlBlock,
} from "../../utils/textBlocksHelpers";
import { getBlogBlockPrimaryStyleOptions } from "../constants";
import imagePlaceholder1 from "./blog_block_1_image_placeholder.png";

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "BlogBlock1",
  props: {
    header: {
      prop: "children",
      type: "children",
      value: [
        createPBlock({
          text: "May 11, 2025 · Community insights",
          paragraphStyle: "p3",
        }),
        createH2Block({
          text: "From passive to participatory: rethinking community value",
        }),
        createPBlock({
          text: "How professional networks can evolve from content delivery to co-creation",
        }),
        createUserCardBlock({
          image: {
            signedId: "",
            placeholder: userImagePlaceholder,
          },
          header: "Olivia Hayes",
          description: "Career dev. coach",
          size: "small",
        }),
        createImageBlock({
          signedId: "",
          placeholder: imagePlaceholder1,
        }),
      ],
    },
    content: {
      prop: "children",
      type: "children",
      value: [
        createH4Block({
          text: "The Shift from Consumption to Contribution",
        }),
        createPBlock({
          text: "In the past, professional networks primarily served as platforms for content consumption. Members would passively receive information, updates, and resources without much interaction. However, the landscape is changing. Today’s networks are evolving into spaces where members actively contribute, share insights, and collaborate on projects.",
        }),
        createQuoteBlock({
          text: "“Community is much more than belonging to something; it’s about doing something together that makes belonging matter.”",
        }),
        createPBlock({
          text: "Transitioning to a participatory model encourages members to actively contribute, fostering a sense of ownership and deeper engagement.",
        }),
        createH4Block({
          text: "Designing for Participation",
          styles: {
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "24",
                marginBottom: "0",
                marginLeft: "0",
                marginRight: "0",
              },
            }),
          },
        }),
        createPBlock({
          text: "To cultivate a participatory environment, communities should:",
        }),
        createUlBlock({
          items: [
            "Facilitate Member-Led Initiatives: Encourage members to lead discussions, workshops, or projects.",
            "Implement Feedback Loops: Regularly solicit and act on member feedback to shape community offerings.",
            "Recognize Contributions: Highlight and celebrate member achievements and inputs.",
          ],
        }),
        createPBlock({
          text: "These strategies not only empower members but also enrich the community's collective knowledge.",
        }),
        createH4Block({
          text: "The Benefits of Active Participation",
          styles: {
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "24",
                marginBottom: "0",
                marginLeft: "0",
                marginRight: "0",
              },
            }),
          },
        }),
        createPBlock({
          text: "Active member engagement leads to:",
        }),
        createUlBlock({
          items: [
            "Enhanced Learning: Members learn from each other's experiences and insights.",
            "Stronger Connections: Collaborative activities build trust and camaraderie.",
            "Increased Retention: Engaged members are more likely to remain active in the community.",
          ],
        }),
        createQuoteBlock({
          text: "“The greatness of a community is most accurately measured by the compassionate actions of its members.”",
        }),
        createH4Block({
          text: "Embracing a Participatory Future",
          styles: {
            margin: getMarginOptions({
              defaultValue: {
                marginTop: "24",
                marginBottom: "0",
                marginLeft: "0",
                marginRight: "0",
              },
            }),
          },
        }),
        createPBlock({
          text: "For professional communities to thrive, embracing participatory practices is essential. By shifting from a top-down approach to one that values every member's input, communities can unlock their full potential.",
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        ...getBlogBlockPrimaryStyleOptions(),
      },
    },
  },
};
